/**
 * Long Animation Frame (LoAF) / long-task monitor — the renderer-side,
 * cross-platform (web + Electron) counterpart to the dev-only diagnostics in
 * `gui/electron/riveros/main/main.js`. That script only runs inside the
 * Electron shell and stays dev-only because several of its pieces (a
 * per-frame `requestAnimationFrame` loop, a 60s heap snapshot interval) are
 * genuinely expensive to run forever in production.
 *
 * This monitor is deliberately the *cheap* subset: `PerformanceObserver`
 * only invokes its callback when the browser itself records a qualifying
 * entry (a frame that actually blocked for >50ms) — there is no polling
 * loop and no steady-state cost, so it's safe to run in production on both
 * platforms. In dev it just console.logs (same shape as the Electron-only
 * diagnostics, for local debugging). In production it reports a
 * rate-limited sample to Sentry — a breadcrumb for every janky-enough frame,
 * and (past a much higher threshold) an actual captured message — so a
 * freeze regression shows up in telemetry before users report it.
 */
import { getSentrySync } from '../services/monitoring/lazySentry';
import { IS_PROD } from './devMode';

// Chrome fires long-animation-frame for any frame >50ms; that's far too
// noisy to ship to Sentry as a breadcrumb on every occurrence in production.
// Only frames this slow (a real, user-visible stall) get reported at all.
const REPORT_THRESHOLD_MS = 200;
// Only frames this slow get a captured Sentry *event* (not just a
// breadcrumb) — reserved for genuinely freeze-adjacent stalls.
const CAPTURE_THRESHOLD_MS = 1000;
// One freeze regression firing repeatedly shouldn't flood Sentry with an
// event per subsequent slow frame in the same session.
const MAX_CAPTURES_PER_SESSION = 5;

let installed = false;
let capturesThisSession = 0;

function describeEntry(entry: any): { durationMs: number; scriptSrc: string } {
  const durationMs = Math.round(entry.duration);
  const scriptSrc = entry.scripts?.length
    ? entry.scripts
        .map((s: any) =>
          `${s.invokerType || '?'}:${s.sourceFunctionName || s.invoker || 'anon'}` +
          (s.sourceURL ? ` (${String(s.sourceURL).split('/').pop()}:${s.sourceCharPosition})` : ''),
        )
        .join(' | ')
    : entry.attribution?.[0]?.containerSrc || entry.attribution?.[0]?.scriptURL || 'unknown';
  return { durationMs, scriptSrc };
}

function handleEntries(entries: PerformanceEntryList): void {
  for (const entry of entries) {
    const { durationMs, scriptSrc } = describeEntry(entry);

    if (!IS_PROD) {
      console.log(`[Perf] Slow frame: ${durationMs}ms scripts=[${scriptSrc}]`);
      continue;
    }

    if (durationMs < REPORT_THRESHOLD_MS) continue;

    try {
      const sentry = getSentrySync() as any;
      if (!sentry) continue; // not loaded yet — this sample is dropped, not queued (freeze telemetry is a sample, not a ledger)
      sentry.addBreadcrumb({
        category: 'perf.long_task',
        message: `slow frame duration=${durationMs}ms scripts=${scriptSrc}`,
        level: 'warning',
        data: { durationMs, scripts: scriptSrc },
      });
      if (durationMs >= CAPTURE_THRESHOLD_MS && capturesThisSession < MAX_CAPTURES_PER_SESSION) {
        capturesThisSession += 1;
        sentry.captureMessage(`Long task: ${durationMs}ms frame (scripts=${scriptSrc})`, 'warning');
      }
    } catch {
      // Monitoring must never itself become a source of errors.
    }
  }
}

/**
 * Installs the observer. Safe to call multiple times (only the first call
 * does anything) and safe to call before Sentry has finished its deferred
 * load — see handleEntries' getSentrySync() null check.
 */
export function initLongTaskMonitor(): void {
  if (installed) return;
  installed = true;
  if (typeof window === 'undefined' || typeof PerformanceObserver === 'undefined') return;

  try {
    new PerformanceObserver((list) => handleEntries(list.getEntries())).observe({
      entryTypes: ['long-animation-frame'],
    });
    return;
  } catch {
    // LoAF unsupported in this browser — fall back to the older longtask API.
  }
  try {
    new PerformanceObserver((list) => handleEntries(list.getEntries())).observe({
      entryTypes: ['longtask'],
    });
  } catch {
    // Neither API supported — nothing to observe.
  }
}
